"use client";

import * as React from "react";
import Image from "next/image";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Icon } from "@/components/ui/icon";
import { openCompose, type ComposeFields } from "@/lib/email-compose";

type Align = "start" | "center" | "end";
type Side = "top" | "bottom" | "left" | "right";

type EmailComposeMenuProps = {
  /** Recipient address the draft is pre-addressed to. */
  email: string;
  /** Trigger element (e.g. a button wrapping the email text). */
  children: React.ReactElement;
  /** Optional pre-filled subject/body/cc/bcc. */
  fields?: ComposeFields;
  align?: Align;
  side?: Side;
};

/**
 * Wraps a trigger so that clicking it opens a follow-up menu letting the user
 * pick which mail client to compose in (Gmail, Outlook, or the OS default),
 * then opens the appropriate pre-addressed compose URL.
 */
export function EmailComposeMenu({
  email,
  children,
  fields,
  align = "start",
  side = "bottom",
}: EmailComposeMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side} className="min-w-56">
        <DropdownMenuLabel className="truncate">
          Email {email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => openCompose("google", email, fields)}>
          <Image
            src="/logos/google.svg"
            alt=""
            width={16}
            height={16}
            className="size-4"
          />
          Gmail
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => openCompose("microsoft", email, fields)}
        >
          <Image
            src="/logos/microsoft.svg"
            alt=""
            width={16}
            height={16}
            className="size-4"
          />
          Outlook
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => openCompose("default", email, fields)}>
          <Icon name="IconEmail1" size={16} className="text-foreground/70" />
          Default Mail Client
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
